import { useMutation } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DesignInput {
  sessionId: string | null;
  messages: ChatMessage[];
  dataset?: {
    name: string;
    sampleRows: Array<Record<string, unknown>>;
    columns: Array<{ name: string; type: string }>;
  };
}

export interface DesignResult {
  sessionId: string;
  html: string;
  css: string | null;
  xlsxSpec: Record<string, unknown> | null;
  libraries: Array<'chartjs' | 'lucide' | 'fontawesome'> | null;
  assistantMessage: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
}

export function useDesignTemplate() {
  const api = useApi();
  return useMutation<DesignResult, Error, DesignInput>({
    mutationFn: (input) =>
      api.fetch('/copilot/templates/design', {
        method: 'POST',
        body: JSON.stringify({
          ...(input.sessionId ? { sessionId: input.sessionId } : {}),
          messages: input.messages,
          ...(input.dataset ? { dataset: input.dataset } : {}),
        }),
      }),
  });
}

export interface PreviewInput {
  html: string;
  css?: string;
  title?: string;
  datasetId?: string;
  columnOrder?: string[];
  params?: Record<string, unknown>;
  sampleRows?: Array<Record<string, unknown>>;
  libraries?: Array<'chartjs' | 'lucide' | 'fontawesome'>;
}

export interface PreviewResult {
  html: string;
  rowCount: number;
  durationMs: number;
}

export function usePreviewHtml() {
  const api = useApi();
  return useMutation<PreviewResult, Error, PreviewInput>({
    mutationFn: (input) =>
      api.fetch('/preview/html', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

// ─── Dashboard design ──────────────────────────────────────────────

export interface DashboardDatasetContext {
  key: string;
  datasetName: string;
  columns: string[];
  sampleRows: Array<Record<string, unknown>>;
}

export interface DashboardDesignInput {
  sessionId: string | null;
  messages: ChatMessage[];
  displayMode: 'dashboard' | 'kiosk';
  refreshIntervalSec: number;
  datasets: DashboardDatasetContext[];
}

export interface DashboardDesignResult {
  sessionId: string;
  html: string;
  css: string | null;
  libraries: Array<'chartjs' | 'lucide' | 'fontawesome'> | null;
  assistantMessage: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
}

export function useDesignDashboard() {
  const api = useApi();
  return useMutation<DashboardDesignResult, Error, DashboardDesignInput>({
    mutationFn: (input) =>
      api.fetch('/copilot/dashboards/design', {
        method: 'POST',
        body: JSON.stringify({
          ...(input.sessionId ? { sessionId: input.sessionId } : {}),
          messages: input.messages,
          displayMode: input.displayMode,
          refreshIntervalSec: input.refreshIntervalSec,
          datasets: input.datasets,
        }),
      }),
  });
}
